import { useState } from 'react';

import { createFileRoute } from '@tanstack/react-router';

import { GroundTruthEditorShell } from '@/features/GroundTruthHub/GroundTruthEditorShell';
import {
    GroundTruthPreview,
    splitEditorPreviewSize,
    wholeFilePreview,
} from '@/features/GroundTruthHub/GroundTruthPreview';
import type { HubPdfJump } from '@/features/GroundTruthHub/HubPdfViewer';
import { PageSplitEditor } from '@/features/GroundTruthHub/PageSplitEditor';
import {
    isNotFoundError,
    useGtFileDocs,
    useGtFileStatus,
    useGtPageSplits,
} from '@/features/GroundTruthHub/api';
import { editorTitle } from '@/features/GroundTruthHub/fileLabels';
import { ingestedFileIdParams } from '@/features/GroundTruthHub/routeParams';
import {
    GroundTruthNotFound,
    groundTruthRouteBoundaries,
} from '@/features/GroundTruthHub/GroundTruthNotFound';

/** Exported so it can be unit-tested with plain props; see `sheets.test.tsx`. */
export function PageSplitRoute({ ingestedFileId }: { ingestedFileId: number }) {
    const { data: docs, error } = useGtFileDocs(ingestedFileId);
    const { data: fileRow } = useGtFileStatus(ingestedFileId);
    const { data: pageSplits } = useGtPageSplits(ingestedFileId);
    const [jump, setJump] = useState<HubPdfJump | null>(null);
    const [previewDefaultSize] = useState(splitEditorPreviewSize);

    if (isNotFoundError(error)) {
        return <GroundTruthNotFound what="This file" />;
    }

    const presignedUrl = fileRow?.fileKind === 'pdf' ? (fileRow.presignedUrl ?? null) : null;
    // Bands come from the saved splits, so they show whether or not the pipeline made documents.
    const wholeFile = presignedUrl ? wholeFilePreview(presignedUrl, pageSplits ?? [], null) : null;
    // A PDF with no documents falls back to its original file, as on the file page.
    const originalFileUrl = presignedUrl && docs?.length === 0 ? presignedUrl : null;
    const showPage = (page: number) =>
        setJump((current) => ({ page, seq: (current?.seq ?? 0) + 1 }));

    return (
        <GroundTruthEditorShell title={editorTitle('Page splits', fileRow)}>
            <GroundTruthPreview
                docId={docs?.[0]?.id ?? null}
                originalFileUrl={originalFileUrl}
                wholeFile={wholeFile}
                defaultMode="file"
                previewDefaultSize={previewDefaultSize}
                jumpTo={jump}
            >
                <PageSplitEditor
                    ingestedFileId={ingestedFileId}
                    onShowPage={wholeFile || originalFileUrl ? showPage : undefined}
                />
            </GroundTruthPreview>
        </GroundTruthEditorShell>
    );
}

function PageSplitRouteComponent() {
    const { ingestedFileId } = Route.useParams();
    return <PageSplitRoute ingestedFileId={ingestedFileId} />;
}

export const Route = createFileRoute('/ground-truth/$ingestedFileId/pages')({
    ...groundTruthRouteBoundaries,
    component: PageSplitRouteComponent,
    params: ingestedFileIdParams,
});
